/* tslint:disable */
/* eslint-disable */
import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';

import { ApiResponseClearChatMemoryResult } from '../models/api-response-clear-chat-memory-result';
import { ApiResponseCompanyPromptDto } from '../models/api-response-company-prompt-dto';
import { ApiResponseConversationAutomationItem } from '../models/api-response-conversation-automation-item';
import { ApiResponseConversationAutomationStatus } from '../models/api-response-conversation-automation-status';
import { ApiResponsePagedResultAssistanceInProgress } from '../models/api-response-paged-result-assistance-in-progress';
import { ApiResponseString } from '../models/api-response-string';
import { clearChatMemory } from '../fn/ai-assistant-api/clear-chat-memory';
import { ClearChatMemory$Params } from '../fn/ai-assistant-api/clear-chat-memory';
import { getCompanyAgent } from '../fn/ai-assistant-api/get-company-agent';
import { GetCompanyAgent$Params } from '../fn/ai-assistant-api/get-company-agent';
import { getConversationAutomationStatus } from '../fn/ai-assistant-api/get-conversation-automation-status';
import { GetConversationAutomationStatus$Params } from '../fn/ai-assistant-api/get-conversation-automation-status';
import { searchAssistanceProgress } from '../fn/ai-assistant-api/search-assistance-progress';
import { SearchAssistanceProgress$Params } from '../fn/ai-assistant-api/search-assistance-progress';
import { setCompanyAgentStatus } from '../fn/ai-assistant-api/set-company-agent-status';
import { SetCompanyAgentStatus$Params } from '../fn/ai-assistant-api/set-company-agent-status';
import { startConversationAiAssistance } from '../fn/ai-assistant-api/start-conversation-ai-assistance';
import { StartConversationAiAssistance$Params } from '../fn/ai-assistant-api/start-conversation-ai-assistance';
import { updateSpecialBotStatus } from '../fn/ai-assistant-api/update-special-bot-status';
import { UpdateSpecialBotStatus$Params } from '../fn/ai-assistant-api/update-special-bot-status';
import { upsertCompanyAgent } from '../fn/ai-assistant-api/upsert-company-agent';
import { UpsertCompanyAgent$Params } from '../fn/ai-assistant-api/upsert-company-agent';

@Injectable({ providedIn: 'root' })
export class AiAssistantApiService extends BaseService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /** Path part for operation `getCompanyAgent()` */
  static readonly GetCompanyAgentPath = '/api/ai-assistant/agent';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getCompanyAgent()` instead.
   *
   * This method doesn't expect any request body.
   */
  getCompanyAgent$Response(params: GetCompanyAgent$Params, context?: HttpContext): Observable<StrictHttpResponse<ApiResponseCompanyPromptDto>> {
    return getCompanyAgent(this.http, this.rootUrl, params, context);
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getCompanyAgent$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getCompanyAgent(params: GetCompanyAgent$Params, context?: HttpContext): Observable<ApiResponseCompanyPromptDto> {
    return this.getCompanyAgent$Response(params, context).pipe(
      map((r: StrictHttpResponse<ApiResponseCompanyPromptDto>): ApiResponseCompanyPromptDto => r.body)
    );
  }

  /** Path part for operation `upsertCompanyAgent()` */
  static readonly UpsertCompanyAgentPath = '/api/ai-assistant/agent';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `upsertCompanyAgent()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  upsertCompanyAgent$Response(params: UpsertCompanyAgent$Params, context?: HttpContext): Observable<StrictHttpResponse<ApiResponseCompanyPromptDto>> {
    return upsertCompanyAgent(this.http, this.rootUrl, params, context);
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `upsertCompanyAgent$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  upsertCompanyAgent(params: UpsertCompanyAgent$Params, context?: HttpContext): Observable<ApiResponseCompanyPromptDto> {
    return this.upsertCompanyAgent$Response(params, context).pipe(
      map((r: StrictHttpResponse<ApiResponseCompanyPromptDto>): ApiResponseCompanyPromptDto => r.body)
    );
  }

  /** Path part for operation `updateSpecialBotStatus()` */
  static readonly UpdateSpecialBotStatusPath = '/api/ai-assistant/update-status';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateSpecialBotStatus()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateSpecialBotStatus$Response(params: UpdateSpecialBotStatus$Params, context?: HttpContext): Observable<StrictHttpResponse<ApiResponseString>> {
    return updateSpecialBotStatus(this.http, this.rootUrl, params, context);
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateSpecialBotStatus$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateSpecialBotStatus(params: UpdateSpecialBotStatus$Params, context?: HttpContext): Observable<ApiResponseString> {
    return this.updateSpecialBotStatus$Response(params, context).pipe(
      map((r: StrictHttpResponse<ApiResponseString>): ApiResponseString => r.body)
    );
  }

  /** Path part for operation `searchAssistanceProgress()` */
  static readonly SearchAssistanceProgressPath = '/api/ai-assistant/search';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `searchAssistanceProgress()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  searchAssistanceProgress$Response(params: SearchAssistanceProgress$Params, context?: HttpContext): Observable<StrictHttpResponse<ApiResponsePagedResultAssistanceInProgress>> {
    return searchAssistanceProgress(this.http, this.rootUrl, params, context);
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `searchAssistanceProgress$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  searchAssistanceProgress(params: SearchAssistanceProgress$Params, context?: HttpContext): Observable<ApiResponsePagedResultAssistanceInProgress> {
    return this.searchAssistanceProgress$Response(params, context).pipe(
      map((r: StrictHttpResponse<ApiResponsePagedResultAssistanceInProgress>): ApiResponsePagedResultAssistanceInProgress => r.body)
    );
  }

  /** Path part for operation `startConversationAiAssistance()` */
  static readonly StartConversationAiAssistancePath = '/api/ai-assistant/conversation/{conversationReference}/start';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `startConversationAiAssistance()` instead.
   *
   * This method doesn't expect any request body.
   */
  startConversationAiAssistance$Response(params: StartConversationAiAssistance$Params, context?: HttpContext): Observable<StrictHttpResponse<ApiResponseConversationAutomationItem>> {
    return startConversationAiAssistance(this.http, this.rootUrl, params, context);
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `startConversationAiAssistance$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  startConversationAiAssistance(params: StartConversationAiAssistance$Params, context?: HttpContext): Observable<ApiResponseConversationAutomationItem> {
    return this.startConversationAiAssistance$Response(params, context).pipe(
      map((r: StrictHttpResponse<ApiResponseConversationAutomationItem>): ApiResponseConversationAutomationItem => r.body)
    );
  }

  /** Path part for operation `setCompanyAgentStatus()` */
  static readonly SetCompanyAgentStatusPath = '/api/ai-assistant/agent/status';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `setCompanyAgentStatus()` instead.
   *
   * This method doesn't expect any request body.
   */
  setCompanyAgentStatus$Response(params: SetCompanyAgentStatus$Params, context?: HttpContext): Observable<StrictHttpResponse<ApiResponseCompanyPromptDto>> {
    return setCompanyAgentStatus(this.http, this.rootUrl, params, context);
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `setCompanyAgentStatus$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  setCompanyAgentStatus(params: SetCompanyAgentStatus$Params, context?: HttpContext): Observable<ApiResponseCompanyPromptDto> {
    return this.setCompanyAgentStatus$Response(params, context).pipe(
      map((r: StrictHttpResponse<ApiResponseCompanyPromptDto>): ApiResponseCompanyPromptDto => r.body)
    );
  }

  /** Path part for operation `getConversationAutomationStatus()` */
  static readonly GetConversationAutomationStatusPath = '/api/ai-assistant/conversation/{conversationReference}/status';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getConversationAutomationStatus()` instead.
   *
   * This method doesn't expect any request body.
   */
  getConversationAutomationStatus$Response(params: GetConversationAutomationStatus$Params, context?: HttpContext): Observable<StrictHttpResponse<ApiResponseConversationAutomationStatus>> {
    return getConversationAutomationStatus(this.http, this.rootUrl, params, context);
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getConversationAutomationStatus$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getConversationAutomationStatus(params: GetConversationAutomationStatus$Params, context?: HttpContext): Observable<ApiResponseConversationAutomationStatus> {
    return this.getConversationAutomationStatus$Response(params, context).pipe(
      map((r: StrictHttpResponse<ApiResponseConversationAutomationStatus>): ApiResponseConversationAutomationStatus => r.body)
    );
  }

  /** Path part for operation `clearChatMemory()` */
  static readonly ClearChatMemoryPath = '/api/ai-assistant/chat-memory';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `clearChatMemory()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  clearChatMemory$Response(params: ClearChatMemory$Params, context?: HttpContext): Observable<StrictHttpResponse<ApiResponseClearChatMemoryResult>> {
    return clearChatMemory(this.http, this.rootUrl, params, context);
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `clearChatMemory$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  clearChatMemory(params: ClearChatMemory$Params, context?: HttpContext): Observable<ApiResponseClearChatMemoryResult> {
    return this.clearChatMemory$Response(params, context).pipe(
      map((r: StrictHttpResponse<ApiResponseClearChatMemoryResult>): ApiResponseClearChatMemoryResult => r.body)
    );
  }

}
